import React, { useState, useEffect, useRef } from 'react';
import { X, AlertTriangle, Loader2, CheckCircle, XCircle, Info } from 'lucide-react';
import { getUserFacingBookingId } from '../utils/userFacingBookingId';

/**
 * Sprint T1 — Tripjack Cancel Booking Modal
 *
 * Walks the user through:
 *   1. Confirm cancellation (entry)
 *   2. Preview charges  → POST /api/tripjack/amendment/charges
 *   3. Submit cancellation → POST /api/tripjack/amendment/submit
 *   4. Poll status → GET /api/tripjack/amendment/{amendmentId}
 *
 * On open, probes GET …/amendment/by-booking/{bookingId}; if an amendment is
 * still REQUESTED/PENDING, jumps straight to polling (covers abandoned modal +
 * background sweeper co-UX). Parent may also pass `resumePollingAmendmentId`.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   bookingId: string (our internal id)
 *   onCancelled?: () => void
 *   resumePollingAmendmentId?: string | null  — optional explicit resume (e.g. admin banner)
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = {
  CONFIRM: 'confirm',
  PREVIEW: 'preview',
  SUBMITTING: 'submitting',
  POLLING: 'polling',
  DONE: 'done',
  ERROR: 'error',
};

function isOpenAmendmentStatus(status) {
  const s = (status || '').toUpperCase();
  return s === 'REQUESTED' || s === 'PENDING';
}

export default function TripjackCancelModal({
  open,
  onClose,
  bookingId,
  onCancelled,
  resumePollingAmendmentId = null,
}) {
  const [introReady, setIntroReady] = useState(false);
  const [step, setStep] = useState(STEPS.CONFIRM);
  const [charges, setCharges] = useState(null);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [amendmentId, setAmendmentId] = useState(null);
  const [pollState, setPollState] = useState(null);
  const [errMsg, setErrMsg] = useState('');
  const [cancellationFareContext, setCancellationFareContext] = useState(null);
  const pollTimer = useRef(null);

  // On open: explicit resume OR probe by-booking for in-flight cancellation
  useEffect(() => {
    if (!open || !bookingId) {
      setIntroReady(false);
      return;
    }

    let cancelled = false;

    const startPolling = (id) => {
      if (cancelled) return;
      setAmendmentId(id);
      setStep(STEPS.POLLING);
      setCharges(null);
      setRemarks('');
      setPollState(null);
      setErrMsg('');
      setIntroReady(true);
    };

    const startFresh = () => {
      if (cancelled) return;
      setStep(STEPS.CONFIRM);
      setCharges(null);
      setRemarks('');
      setAmendmentId(null);
      setPollState(null);
      setErrMsg('');
      setIntroReady(true);
    };

    (async () => {
      setIntroReady(false);
      setCancellationFareContext(null);

      if (resumePollingAmendmentId) {
        try {
          const rctx = await fetch(
            `${API}/tripjack/amendment/by-booking/${encodeURIComponent(bookingId)}`,
            { credentials: 'include' },
          );
          if (rctx.ok && !cancelled) {
            const dctx = await rctx.json();
            if (dctx.cancellation_fare_context) setCancellationFareContext(dctx.cancellation_fare_context);
          }
        } catch (e) {
          console.warn('Fare context fetch (resume) failed:', e?.message || e);
        }
        if (!cancelled) startPolling(resumePollingAmendmentId);
        return;
      }

      try {
        const resp = await fetch(
          `${API}/tripjack/amendment/by-booking/${encodeURIComponent(bookingId)}`,
          { credentials: 'include' },
        );
        if (!resp.ok || cancelled) {
          startFresh();
          return;
        }
        const data = await resp.json();
        if (data.cancellation_fare_context && !cancelled) {
          setCancellationFareContext(data.cancellation_fare_context);
        }
        const items = data.amendments || [];
        const openAmend = items.find((a) => isOpenAmendmentStatus(a.amendment_status));
        if (openAmend?.amendment_id && !cancelled) {
          startPolling(openAmend.amendment_id);
          return;
        }
        if (!cancelled) {
          setStep(STEPS.CONFIRM);
          setCharges(null);
          setRemarks('');
          setAmendmentId(null);
          setPollState(null);
          setErrMsg('');
          setIntroReady(true);
        }
        return;
      } catch (e) {
        console.warn('Amendment by-booking probe failed:', e?.message || e);
      }
      if (!cancelled) startFresh();
    })();

    return () => {
      cancelled = true;
    };
  }, [open, bookingId, resumePollingAmendmentId]);

  // Cleanup poll timer
  useEffect(() => () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
  }, []);

  // Poll amendment status when amendmentId is set + step is polling
  useEffect(() => {
    if (!open || !introReady) return;
    if (step !== STEPS.POLLING || !amendmentId) return;
    let attempts = 0;
    const MAX = 12;

    const tick = async () => {
      attempts += 1;
      try {
        const resp = await fetch(`${API}/tripjack/amendment/${amendmentId}`, {
          credentials: 'include',
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.detail || 'Status check failed');
        setPollState(data);
        const s = (data.status || '').toUpperCase();
        if (s === 'SUCCESS') {
          setStep(STEPS.DONE);
          if (onCancelled) onCancelled();
          return;
        }
        if (s === 'REJECTED') {
          setErrMsg('Cancellation was rejected by the airline or fare rules. Please contact support.');
          setStep(STEPS.ERROR);
          return;
        }
        if (attempts >= MAX) {
          setErrMsg(
            `Cancellation is still in progress (status: ${s}). Our system will keep checking in the background — refresh this booking in a few minutes.`,
          );
          setStep(STEPS.ERROR);
          return;
        }
        pollTimer.current = setTimeout(tick, 10_000);
      } catch (e) {
        setErrMsg(e.message);
        setStep(STEPS.ERROR);
      }
    };

    tick();
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [open, introReady, step, amendmentId, onCancelled]);

  if (!open) return null;

  if (!introReady) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
        data-testid="tripjack-cancel-modal"
      >
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 text-center">
          <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-700">Checking cancellation status…</p>
          <p className="text-xs text-gray-500 mt-2">Looking for any in-progress cancellation on this booking.</p>
        </div>
      </div>
    );
  }

  const fetchCharges = async () => {
    setChargesLoading(true);
    setErrMsg('');
    try {
      const resp = await fetch(`${API}/tripjack/amendment/charges`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId,
          type: 'CANCELLATION',
          remarks: 'Preview cancellation charges',
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        const msg = typeof data?.detail === 'string' ? data.detail : (data?.detail?.message || 'Could not fetch charges');
        throw new Error(msg);
      }
      if (data.cancellation_fare_context) {
        setCancellationFareContext(data.cancellation_fare_context);
      }
      setCharges(data);
      setStep(STEPS.PREVIEW);
    } catch (e) {
      console.warn('Amendment charges preview failed:', e.message);
      setCharges({ trips: [], _previewUnavailable: true, _reason: e.message });
      setStep(STEPS.PREVIEW);
    } finally {
      setChargesLoading(false);
    }
  };

  const submitCancellation = async () => {
    if (!remarks || remarks.trim().length < 3) {
      setErrMsg('Please add a reason for cancellation (3+ characters).');
      return;
    }
    setStep(STEPS.SUBMITTING);
    setErrMsg('');
    try {
      const resp = await fetch(`${API}/tripjack/amendment/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId,
          type: 'CANCELLATION',
          remarks: remarks.trim(),
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        if (resp.status === 409 && data?.detail?.amendment_id) {
          setAmendmentId(data.detail.amendment_id);
          setStep(STEPS.POLLING);
          return;
        }
        const msg = typeof data?.detail === 'string' ? data.detail : (data?.detail?.message || 'Submit failed');
        throw new Error(msg);
      }
      setAmendmentId(data.amendment_id);
      setStep(STEPS.POLLING);
    } catch (e) {
      setErrMsg(e.message);
      setStep(STEPS.ERROR);
    }
  };

  const renderCharges = () => {
    if (charges?._previewUnavailable) {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          <strong>Charge preview unavailable</strong>
          <p className="mt-1">The booking platform could not return cancellation charges right now. You can still proceed — final refund will be confirmed by the airline after submission.</p>
        </div>
      );
    }
    const trips = charges?.trips || [];
    if (trips.length === 0) {
      return <p className="text-sm text-gray-600">No charge breakdown returned. Submit to proceed.</p>;
    }
    return (
      <div className="space-y-3">
        {trips.map((trip, i) => {
          const paxBlocks = Object.entries(trip.amendmentInfo || {});
          return (
            <div key={i} className="border border-gray-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-gray-700">
                {trip.src} → {trip.dest}
                {trip.departureDate && (
                  <span className="text-xs text-gray-500 ml-2">on {trip.departureDate}</span>
                )}
              </p>
              {paxBlocks.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {paxBlocks.map(([paxType, info]) => (
                    <div key={paxType} className="text-xs flex justify-between bg-gray-50 px-2 py-1.5 rounded">
                      <span className="text-gray-600">{paxType}</span>
                      <span className="text-gray-800">
                        Charges: <strong className="text-red-600">₹{(info.amendmentCharges || 0).toLocaleString('en-IN')}</strong>
                        {' · '}
                        Refund: <strong className="text-emerald-600">₹{(info.refundAmount || 0).toLocaleString('en-IN')}</strong>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mt-1">No pax breakdown available.</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderAcfFlexFarePanel = () => {
    const ctx = cancellationFareContext;
    if (!ctx?.is_acf_flex) return null;
    const bullets = ctx.rule_bullets || [];
    const notices = ctx.notices || [];
    const hours = ctx.hours_until_first_departure;
    return (
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/80 p-3 text-xs text-indigo-950 space-y-2">
        <p className="font-semibold text-sm text-indigo-900">{ctx.display_fare_name || 'ACF Flex'} — fare rules (cancellation)</p>
        {typeof hours === 'number' && (
          <p className="text-indigo-800">
            First flight in approximately <strong>{hours.toFixed(1)}</strong> hours
            {ctx.within_24h_before_first_departure ? ' (inside 24 hours before departure)' : ''}
            .
          </p>
        )}
        {notices.map((n, i) => (
          <p key={`n-${i}`} className="text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">{n}</p>
        ))}
        <ul className="list-disc pl-4 space-y-1 text-indigo-900">
          {bullets.map((b, i) => (
            <li key={`b-${i}`}>{b}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      data-testid="tripjack-cancel-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-bold text-gray-800">Cancel Booking</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close" data-testid="cancel-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === STEPS.CONFIRM && (
            <>
              <div className="flex gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
                <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                <p>If you already submitted a cancellation and this page still shows &quot;confirmed&quot;, open this dialog again — we detect in-progress cancellations automatically.</p>
              </div>
              <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold">This will cancel the ticket with the airline.</p>
                  <p className="text-xs mt-1">Cancellation charges may apply per the fare rules. We&apos;ll preview the breakdown before you confirm.</p>
                </div>
              </div>
              {renderAcfFlexFarePanel()}
              <p className="text-sm text-gray-700">Booking ID: <span className="font-mono font-semibold">{getUserFacingBookingId({ booking_id: bookingId, id: bookingId })}</span></p>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  data-testid="cancel-modal-back"
                >
                  Back
                </button>
                <button
                  onClick={fetchCharges}
                  disabled={chargesLoading}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="cancel-modal-preview-btn"
                >
                  {chargesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Preview Charges
                </button>
              </div>
            </>
          )}

          {step === STEPS.PREVIEW && (
            <>
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Cancellation Charges Preview</h4>
                {renderAcfFlexFarePanel()}
                {renderCharges()}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase mb-2">
                  Reason for cancellation *
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={3}
                  placeholder="e.g. Customer plan changed, refund to wallet"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="cancel-modal-remarks"
                />
                {errMsg && <p className="text-xs text-red-600 mt-1">{errMsg}</p>}
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Keep Booking
                </button>
                <button
                  onClick={submitCancellation}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                  data-testid="cancel-modal-confirm-btn"
                >
                  Confirm Cancellation
                </button>
              </div>
            </>
          )}

          {(step === STEPS.SUBMITTING || step === STEPS.POLLING) && (
            <div className="text-center py-6">
              <Loader2 className="w-10 h-10 text-red-600 animate-spin mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-800">
                {step === STEPS.SUBMITTING ? 'Submitting cancellation…' : 'Awaiting airline confirmation…'}
              </p>
              {amendmentId && (
                <p className="text-xs text-gray-500 mt-1">Reference: {amendmentId}</p>
              )}
              {pollState?.status && (
                <p className="text-xs text-gray-500 mt-1">Status: {pollState.status}</p>
              )}
              <p className="text-xs text-gray-400 mt-2">Confirmation usually completes within 30–60 seconds. You can close this window — we keep syncing in the background.</p>
            </div>
          )}

          {step === STEPS.DONE && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-base font-semibold text-gray-800">Booking Cancelled</p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-3 text-left text-sm space-y-1">
                <p><span className="text-gray-600">Charges:</span> <strong>₹{(pollState?.amendment_charges || 0).toLocaleString('en-IN')}</strong></p>
                <p><span className="text-gray-600">Refund:</span> <strong className="text-emerald-700">₹{(pollState?.refundable_amount || 0).toLocaleString('en-IN')}</strong></p>
                <p><span className="text-gray-600">Reference:</span> <span className="font-mono">{amendmentId}</span></p>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Refund will be credited per your original payment method. Wallet refunds are instant.
              </p>
              <button
                onClick={onClose}
                className="mt-4 w-full px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                data-testid="cancel-modal-done-btn"
              >
                Close
              </button>
            </div>
          )}

          {step === STEPS.ERROR && (
            <div className="text-center py-4">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-base font-semibold text-gray-800">Cancellation Issue</p>
              <p className="text-sm text-gray-600 mt-2">{errMsg}</p>
              <button
                onClick={onClose}
                className="mt-4 w-full px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 font-medium"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
